import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { FormField, FieldType } from "@/mocks/leadCaptureFormsData";
import { fieldTypeLabels, fieldTypeIcons, basicFieldTypes, specialFieldTypes, hiddenFieldTypes, defaultFormFields } from "@/mocks/leadCaptureFormsData";
import { fetchApi } from "@/services/apiService";

type Props = {
  initialTitle?: string;
  initialSubtitle?: string;
  initialFields?: FormField[];
  initialSubmitText?: string;
  initialPrivacyCheckbox?: boolean;
  initialSuccessMessage?: string;
  initialMappedProjectId?: string | null;
  formId?: string;
  isEdit?: boolean;
};

export default function FormBuilder({
  initialTitle = "Contact Us",
  initialSubtitle = "Fill in your details and we'll get back to you shortly",
  initialFields = defaultFormFields,
  initialSubmitText = "Submit Inquiry",
  initialPrivacyCheckbox = true,
  initialSuccessMessage = "Thank you! We'll contact you shortly.",
  initialMappedProjectId = null,
  formId,
  isEdit = false,
}: Props) {
  const navigate = useNavigate();
  const [formTitle, setFormTitle] = useState(initialTitle);
  const [formSubtitle, setFormSubtitle] = useState(initialSubtitle);
  const [fields, setFields] = useState<FormField[]>(initialFields);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [submitButtonText, setSubmitButtonText] = useState(initialSubmitText);
  const [privacyPolicyCheckbox, setPrivacyPolicyCheckbox] = useState(initialPrivacyCheckbox);
  const [successMessage, setSuccessMessage] = useState(initialSuccessMessage);
  const [mappedProjectId, setMappedProjectId] = useState<string | null>(initialMappedProjectId);
  const [projectsList, setProjectsList] = useState<any[]>([]);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Active mode: "edit" (builder canvas) vs "preview" (interactive test form)
  const [activeMode, setActiveMode] = useState<"edit" | "preview">("edit");
  const [savedFormId, setSavedFormId] = useState<string | undefined>(formId);

  // Test Submission state
  const [testValues, setTestValues] = useState<Record<string, any>>({});
  const [testPrivacyAgreed, setTestPrivacyAgreed] = useState(false);
  const [testErrors, setTestErrors] = useState<Record<string, string>>({});
  const [isTestSubmitting, setIsTestSubmitting] = useState(false);
  const [testSubmitted, setTestSubmitted] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  useEffect(() => {
    fetchApi<any[]>('/projects', undefined, [])
      .then((res) => setProjectsList(Array.isArray(res) ? res : (res as any)?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (formId) setSavedFormId(formId);
  }, [formId]);

  const selectedField = fields.find((f) => f.id === selectedFieldId) || null;

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const addField = (type: FieldType) => {
    const newField: FormField = {
      id: `f-${Date.now()}`,
      type,
      label: fieldTypeLabels[type],
      placeholder: "",
      required: false,
      width: "full",
      helpText: "",
      options: type === "dropdown" || type === "radio" || type === "checkbox" || type === "property_type" || type === "budget_range"
        ? ["Option 1", "Option 2"]
        : [],
    };
    setFields([...fields, newField]);
    setSelectedFieldId(newField.id);
  };

  const updateField = (id: string, updates: Partial<FormField>) => {
    setFields(fields.map((f) => (f.id === id ? { ...f, ...updates } : f)));
  };

  const removeField = (id: string) => {
    setFields(fields.filter((f) => f.id !== id));
    if (selectedFieldId === id) setSelectedFieldId(null);
  };

  const moveField = (id: string, direction: "up" | "down") => {
    const idx = fields.findIndex((f) => f.id === id);
    if (idx === -1) return;
    if (direction === "up" && idx === 0) return;
    if (direction === "down" && idx === fields.length - 1) return;
    const newFields = [...fields];
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    [newFields[idx], newFields[swapIdx]] = [newFields[swapIdx], newFields[idx]];
    setFields(newFields);
  };

  const addOption = (fieldId: string) => {
    setFields(fields.map((f) => {
      if (f.id === fieldId) {
        return { ...f, options: [...f.options, `Option ${f.options.length + 1}`] };
      }
      return f;
    }));
  };

  const updateOption = (fieldId: string, idx: number, value: string) => {
    setFields(fields.map((f) => {
      if (f.id === fieldId) {
        const newOptions = [...f.options];
        newOptions[idx] = value;
        return { ...f, options: newOptions };
      }
      return f;
    }));
  };

  const removeOption = (fieldId: string, idx: number) => {
    setFields(fields.map((f) => {
      if (f.id === fieldId) {
        return { ...f, options: f.options.filter((_, i) => i !== idx) };
      }
      return f;
    }));
  };

  const handleSaveForm = async (status: "Active" | "Inactive" | "Draft"): Promise<string | null> => {
    try {
      const endpoint = isEdit && savedFormId ? `/lead-capture/forms/${savedFormId}` : "/lead-capture/forms";
      const method = isEdit && savedFormId ? "PUT" : "POST";

      const res: any = await fetchApi(endpoint, {
        method,
        body: JSON.stringify({
          title: formTitle,
          description: formSubtitle,
          fields,
          status: status === "Draft" ? "Inactive" : "Active",
          submit_button_text: submitButtonText,
          privacy_policy_checkbox: privacyPolicyCheckbox,
          success_message: successMessage,
          project_id: mappedProjectId,
        }),
      });

      const newId = res?.data?.id || res?.id || savedFormId;
      if (newId) {
        setSavedFormId(String(newId));
      }

      showToast(`Form ${isEdit ? "updated" : "published"} successfully!`);
      return String(newId || "");
    } catch (err: any) {
      showToast(err.message || "Failed to save form");
      return null;
    }
  };

  const handleTestInputChange = (label: string, value: any) => {
    setTestValues((prev) => ({ ...prev, [label]: value }));
    if (testErrors[label]) {
      setTestErrors((prev) => {
        const next = { ...prev };
        delete next[label];
        return next;
      });
    }
  };

  const handleTestCheckboxChange = (label: string, opt: string, checked: boolean) => {
    const current = Array.isArray(testValues[label]) ? testValues[label] : [];
    const next = checked ? [...current, opt] : current.filter((x: string) => x !== opt);
    handleTestInputChange(label, next);
  };

  // Submit test inquiry
  const handleTestSubmit = async () => {
    const newErrors: Record<string, string> = {};

    for (const f of fields) {
      const val = testValues[f.label];
      if (f.required) {
        if (val === undefined || val === null || String(val).trim() === "" || (Array.isArray(val) && val.length === 0)) {
          newErrors[f.label] = `${f.label} is required`;
        }
      }
      if (val && f.type === "phone") {
        const digits = String(val).replace(/\D/g, "");
        if (digits.length < 10) {
          newErrors[f.label] = "Phone number must be at least 10 digits";
        }
      }
      if (val && f.type === "email") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(String(val))) {
          newErrors[f.label] = "Please enter a valid email address";
        }
      }
    }

    if (privacyPolicyCheckbox && !testPrivacyAgreed) {
      newErrors["privacy"] = "Please agree to the privacy policy to submit";
    }

    if (Object.keys(newErrors).length > 0) {
      setTestErrors(newErrors);
      showToast("Please fill all required fields properly");
      return;
    }

    setIsTestSubmitting(true);
    setTestErrors({});

    try {
      let targetId = savedFormId;
      // Auto-save form if not created yet
      if (!targetId) {
        targetId = (await handleSaveForm("Active")) || undefined;
      }

      if (!targetId) {
        throw new Error("Unable to identify form ID for submission");
      }

      await fetchApi("/lead-capture/submit", {
        method: "POST",
        body: JSON.stringify({
          form_id: targetId,
          data: testValues,
        }),
      });

      setTestSubmitted(true);
      showToast("Inquiry submitted successfully! Lead recorded in CRM.");
    } catch (err: any) {
      showToast(err.message || "Failed to submit inquiry");
      setTestErrors({ form: err.message || "Failed to submit" });
    } finally {
      setIsTestSubmitting(false);
    }
  };

  const handleResetTestForm = () => {
    setTestValues({});
    setTestPrivacyAgreed(false);
    setTestErrors({});
    setTestSubmitted(false);
  };

  const handleCanvasSubmitClick = () => {
    if (activeMode === "edit") {
      setActiveMode("preview");
      showToast("Switched to Live Preview! You can now test the form inputs and submit.");
      return;
    }
    handleTestSubmit();
  };

  const handleCopyPublicLink = () => {
    if (!savedFormId) return;
    const url = `${window.location.origin}/forms/${savedFormId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedLink(true);
      showToast("Public form link copied to clipboard!");
      setTimeout(() => setCopiedLink(false), 2000);
    });
  };

  // Render field preview in Design mode
  const renderFieldDesign = (field: FormField) => {
    const isSelected = selectedFieldId === field.id;
    return (
      <div
        key={field.id}
        onClick={() => setSelectedFieldId(field.id)}
        className={`bg-white rounded-xl border p-4 mb-3 cursor-pointer transition-all ${
          isSelected ? "border-indigo-500 ring-2 ring-indigo-100 shadow-xs" : "border-gray-200 hover:border-gray-300"
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="flex flex-col gap-0.5 cursor-grab text-gray-300">
            <i className="ri-draggable text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 mb-1.5">
              <span className="text-sm font-semibold text-foreground-950">{field.label}</span>
              {field.required && <span className="text-red-500 text-xs font-bold">*</span>}
            </div>
            {field.type === "textarea" ? (
              <div className="w-full h-16 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-400">
                {field.placeholder || "Write here..."}
              </div>
            ) : field.type === "dropdown" || field.type === "property_type" || field.type === "budget_range" ? (
              <div className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-400 flex items-center justify-between">
                <span>{field.placeholder || (field.options?.length ? `Options: ${field.options.slice(0, 3).join(", ")}...` : "Select an option")}</span>
                <i className="ri-arrow-down-s-line text-gray-400" />
              </div>
            ) : field.type === "radio" ? (
              <div className="flex flex-wrap gap-3">
                {field.options.map((opt, i) => (
                  <label key={i} className="flex items-center gap-1.5 text-xs text-gray-600">
                    <div className="w-3.5 h-3.5 rounded-full border border-gray-300" />
                    {opt}
                  </label>
                ))}
              </div>
            ) : field.type === "checkbox" ? (
              <div className="flex flex-wrap gap-3">
                {field.options.map((opt, i) => (
                  <label key={i} className="flex items-center gap-1.5 text-xs text-gray-600">
                    <div className="w-3.5 h-3.5 rounded border border-gray-300" />
                    {opt}
                  </label>
                ))}
              </div>
            ) : field.type === "date" || field.type === "site_visit_date" ? (
              <div className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-400 flex items-center justify-between">
                <span>{field.placeholder || "Select date"}</span>
                <i className="ri-calendar-line text-gray-400" />
              </div>
            ) : (
              <div className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-400">
                {field.placeholder || `Enter ${field.label.toLowerCase()}...`}
              </div>
            )}
            {field.helpText && (
              <p className="text-[11px] text-gray-400 mt-1">{field.helpText}</p>
            )}
          </div>
          <div className="flex flex-col gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); moveField(field.id, "up"); }}
              className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 cursor-pointer transition-colors"
              title="Move Up"
            >
              <i className="ri-arrow-up-s-line text-gray-400 text-xs" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); moveField(field.id, "down"); }}
              className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 cursor-pointer transition-colors"
              title="Move Down"
            >
              <i className="ri-arrow-down-s-line text-gray-400 text-xs" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); removeField(field.id); }}
              className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-50 cursor-pointer transition-colors"
              title="Delete Field"
            >
              <i className="ri-delete-bin-line text-red-400 text-xs" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render interactive field in Live Preview / Test mode
  const renderFieldInteractive = (field: FormField) => {
    const val = testValues[field.label];
    const err = testErrors[field.label];

    return (
      <div key={field.id} className="bg-white rounded-xl border border-gray-200 p-4 mb-3 shadow-2xs">
        <label className="block text-sm font-semibold text-foreground-950 mb-1.5">
          {field.label}
          {field.required && <span className="text-red-500 ml-1 font-bold">*</span>}
        </label>

        {field.type === "textarea" ? (
          <textarea
            rows={3}
            value={val || ""}
            onChange={(e) => handleTestInputChange(field.label, e.target.value)}
            placeholder={field.placeholder || "Write here..."}
            className={`w-full text-sm border rounded-lg px-3 py-2 outline-none transition-colors ${
              err ? "border-red-400 bg-red-50/20" : "border-gray-200 focus:border-indigo-600 bg-white"
            }`}
          />
        ) : field.type === "dropdown" || field.type === "property_type" || field.type === "budget_range" ? (
          <div className="relative">
            <select
              value={val || ""}
              onChange={(e) => handleTestInputChange(field.label, e.target.value)}
              className={`w-full text-sm border rounded-lg px-3 py-2 outline-none appearance-none transition-colors cursor-pointer ${
                err ? "border-red-400 bg-red-50/20" : "border-gray-200 focus:border-indigo-600 bg-white"
              }`}
            >
              <option value="">{field.placeholder || "Select an option"}</option>
              {field.options.map((opt, i) => (
                <option key={i} value={opt}>{opt}</option>
              ))}
            </select>
            <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        ) : field.type === "radio" ? (
          <div className="flex flex-wrap gap-3 pt-1">
            {field.options.map((opt, i) => (
              <label key={i} className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name={field.id}
                  value={opt}
                  checked={val === opt}
                  onChange={() => handleTestInputChange(field.label, opt)}
                  className="text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                />
                {opt}
              </label>
            ))}
          </div>
        ) : field.type === "checkbox" ? (
          <div className="flex flex-wrap gap-3 pt-1">
            {field.options.map((opt, i) => {
              const isChecked = Array.isArray(val) && val.includes(opt);
              return (
                <label key={i} className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    value={opt}
                    checked={isChecked}
                    onChange={(e) => handleTestCheckboxChange(field.label, opt, e.target.checked)}
                    className="text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
                  />
                  {opt}
                </label>
              );
            })}
          </div>
        ) : field.type === "date" || field.type === "site_visit_date" ? (
          <input
            type="date"
            value={val || ""}
            onChange={(e) => handleTestInputChange(field.label, e.target.value)}
            className={`w-full text-sm border rounded-lg px-3 py-2 outline-none transition-colors ${
              err ? "border-red-400 bg-red-50/20" : "border-gray-200 focus:border-indigo-600 bg-white"
            }`}
          />
        ) : field.type === "number" ? (
          <input
            type="number"
            value={val || ""}
            onChange={(e) => handleTestInputChange(field.label, e.target.value)}
            placeholder={field.placeholder || "Enter number..."}
            className={`w-full text-sm border rounded-lg px-3 py-2 outline-none transition-colors ${
              err ? "border-red-400 bg-red-50/20" : "border-gray-200 focus:border-indigo-600 bg-white"
            }`}
          />
        ) : field.type === "phone" ? (
          <input
            type="tel"
            value={val || ""}
            onChange={(e) => handleTestInputChange(field.label, e.target.value)}
            placeholder={field.placeholder || "Enter 10-digit phone number..."}
            className={`w-full text-sm border rounded-lg px-3 py-2 outline-none transition-colors ${
              err ? "border-red-400 bg-red-50/20" : "border-gray-200 focus:border-indigo-600 bg-white"
            }`}
          />
        ) : (
          <input
            type={field.type === "email" ? "email" : "text"}
            value={val || ""}
            onChange={(e) => handleTestInputChange(field.label, e.target.value)}
            placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}...`}
            className={`w-full text-sm border rounded-lg px-3 py-2 outline-none transition-colors ${
              err ? "border-red-400 bg-red-50/20" : "border-gray-200 focus:border-indigo-600 bg-white"
            }`}
          />
        )}

        {field.helpText && (
          <p className="text-[11px] text-gray-400 mt-1">{field.helpText}</p>
        )}
        {err && (
          <p className="text-xs text-red-500 mt-1 font-medium">{err}</p>
        )}
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Toast */}
      {toastMessage && (
        <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white rounded-xl px-4 py-2.5 shadow-xl text-sm flex items-center gap-2 border border-gray-800 animate-fade-in">
          <i className="ri-information-line text-indigo-400" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Top Bar */}
      <div className="bg-white border-b border-gray-200 px-4 md:px-6 py-3 flex items-center justify-between gap-3 flex-shrink-0 shadow-xs">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/lead-capture")}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className="ri-arrow-left-line" /> Back
          </button>
          <input
            type="text"
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            className="text-lg font-bold text-foreground-950 border-b-2 border-transparent focus:border-indigo-500 outline-none bg-transparent px-1"
            placeholder="Untitled Form"
          />
        </div>

        <div className="flex items-center gap-2">
          {savedFormId && (
            <div className="hidden sm:flex items-center gap-1 mr-2">
              <a
                href={`/forms/${savedFormId}`}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 border border-indigo-200 rounded-lg whitespace-nowrap flex items-center gap-1 cursor-pointer transition-colors"
                title="Open Public Form in new tab"
              >
                <i className="ri-external-link-line" /> Open Live Form
              </a>
              <button
                type="button"
                onClick={handleCopyPublicLink}
                className="px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 border border-gray-200 rounded-lg whitespace-nowrap flex items-center gap-1 cursor-pointer transition-colors"
                title="Copy public link"
              >
                <i className={copiedLink ? "ri-check-line text-green-600" : "ri-file-copy-line"} />
                {copiedLink ? "Copied!" : "Copy Link"}
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={() => handleSaveForm("Draft")}
            className="px-3 py-1.5 text-xs font-semibold text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 whitespace-nowrap cursor-pointer transition-colors"
          >
            Save Draft
          </button>
          <button
            type="button"
            onClick={() => handleSaveForm("Active")}
            className="px-4 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 whitespace-nowrap cursor-pointer transition-colors shadow-xs"
          >
            Publish
          </button>
        </div>
      </div>

      {/* 3-Column Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT — Field Library */}
        <div className="w-[260px] bg-white border-r border-gray-200 p-4 overflow-y-auto flex-shrink-0">
          <h3 className="text-xs font-semibold text-foreground-950 uppercase tracking-wider mb-3">Add Fields</h3>

          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Basic Fields</p>
          <div className="space-y-1.5 mb-4">
            {basicFieldTypes.map((type) => (
              <button
                key={type}
                onClick={() => addField(type)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-foreground-700 hover:bg-indigo-50 hover:border-indigo-300 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className={`${fieldTypeIcons[type]} text-gray-500 w-4 h-4 flex items-center justify-center`} />
                {fieldTypeLabels[type]}
              </button>
            ))}
          </div>

          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Special Fields</p>
          <div className="space-y-1.5 mb-4">
            {specialFieldTypes.map((type) => (
              <button
                key={type}
                onClick={() => addField(type)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-700 hover:bg-indigo-100 cursor-pointer transition-colors whitespace-nowrap"
              >
                <i className={`${fieldTypeIcons[type]} w-4 h-4 flex items-center justify-center`} />
                {fieldTypeLabels[type]}
              </button>
            ))}
          </div>

          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Hidden Fields</p>
          <div className="space-y-1.5">
            {hiddenFieldTypes.map((h) => (
              <div key={h.type} className="flex items-center gap-2 px-3 py-2 text-xs text-gray-400 bg-gray-50/50 rounded-lg border border-dashed border-gray-200">
                <i className="ri-eye-off-line w-4 h-4 flex items-center justify-center" />
                {h.label}
              </div>
            ))}
          </div>
        </div>

        {/* CENTER — Form Canvas */}
        <div className="flex-1 bg-slate-50 border-r border-gray-200 p-6 overflow-y-auto">
          <div className="max-w-lg mx-auto">
            {/* Mode Switcher Banner */}
            <div className="bg-white rounded-xl border border-gray-200 p-2 mb-4 flex items-center justify-between shadow-2xs">
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setActiveMode("edit")}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg cursor-pointer transition-all flex items-center gap-1.5 ${
                    activeMode === "edit"
                      ? "bg-indigo-600 text-white shadow-xs"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <i className="ri-edit-line" /> Form Builder (Design)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setActiveMode("preview");
                    showToast("Switched to Live Preview! You can now test inputs and submit.");
                  }}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg cursor-pointer transition-all flex items-center gap-1.5 ${
                    activeMode === "preview"
                      ? "bg-indigo-600 text-white shadow-xs"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <i className="ri-play-circle-line" /> Live Preview & Test
                </button>
              </div>

              {savedFormId && (
                <a
                  href={`/forms/${savedFormId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 px-2 py-1"
                >
                  <span>Public Form</span>
                  <i className="ri-external-link-line" />
                </a>
              )}
            </div>

            {/* Form Header Card */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4 shadow-2xs">
              <div className="flex items-center justify-between mb-2">
                {activeMode === "edit" ? (
                  <input
                    type="text"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    className="text-xl font-bold text-foreground-950 outline-none bg-transparent w-full"
                    placeholder="Form Title"
                  />
                ) : (
                  <h2 className="text-xl font-bold text-foreground-950">{formTitle || "Untitled Form"}</h2>
                )}
                <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0 ml-3">
                  <i className="ri-file-list-3-line text-lg" />
                </div>
              </div>

              {activeMode === "edit" ? (
                <input
                  type="text"
                  value={formSubtitle}
                  onChange={(e) => setFormSubtitle(e.target.value)}
                  className="text-sm text-foreground-700 outline-none bg-transparent w-full"
                  placeholder="Form subtitle / description"
                />
              ) : (
                <p className="text-sm text-foreground-700">{formSubtitle}</p>
              )}
            </div>

            {/* Test Submitted Success Screen */}
            {testSubmitted ? (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center shadow-xs animate-fadeIn mb-4">
                <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-checkbox-circle-fill text-2xl" />
                </div>
                <h3 className="text-lg font-bold text-green-900 mb-1">Inquiry Submitted Successfully!</h3>
                <p className="text-xs text-green-700 max-w-sm mx-auto mb-4">{successMessage}</p>
                <div className="flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={handleResetTestForm}
                    className="px-3.5 py-1.5 text-xs font-semibold text-green-800 bg-white border border-green-300 rounded-lg hover:bg-green-100/50 cursor-pointer transition-colors"
                  >
                    <i className="ri-refresh-line mr-1" /> Test Again
                  </button>
                  {savedFormId && (
                    <button
                      type="button"
                      onClick={() => navigate(`/lead-capture/${savedFormId}/submissions`)}
                      className="px-3.5 py-1.5 text-xs font-semibold text-white bg-green-700 hover:bg-green-800 rounded-lg cursor-pointer transition-colors"
                    >
                      <i className="ri-eye-line mr-1" /> View Submissions
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* Fields */}
                {fields.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300 text-gray-400">
                    <i className="ri-drag-drop-line text-3xl block mb-2" />
                    <p className="text-sm font-medium">Click a field from the left panel to add it</p>
                  </div>
                ) : (
                  fields.map(activeMode === "preview" ? renderFieldInteractive : renderFieldDesign)
                )}

                {/* Form Footer with Interactive Submit Button */}
                <div className="bg-white rounded-xl border border-gray-200 p-5 mt-4 shadow-2xs">
                  {testErrors["form"] && (
                    <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600 flex items-center gap-2">
                      <i className="ri-error-warning-line text-red-500" />
                      {testErrors["form"]}
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleCanvasSubmitClick}
                    disabled={isTestSubmitting}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99] text-white rounded-xl px-6 py-3.5 text-center text-sm font-semibold cursor-pointer shadow-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {isTestSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-base" />
                        <span>Submitting Inquiry...</span>
                      </>
                    ) : (
                      <>
                        <span>{submitButtonText}</span>
                        {activeMode === "edit" ? (
                          <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full font-normal uppercase tracking-wider">
                            Click to Test Submit
                          </span>
                        ) : (
                          <i className="ri-arrow-right-line" />
                        )}
                      </>
                    )}
                  </button>

                  {privacyPolicyCheckbox && (
                    <div className="mt-3">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={activeMode === "preview" ? testPrivacyAgreed : true}
                          onChange={(e) => {
                            if (activeMode === "preview") {
                              setTestPrivacyAgreed(e.target.checked);
                              if (testErrors["privacy"]) {
                                setTestErrors((prev) => {
                                  const n = { ...prev };
                                  delete n["privacy"];
                                  return n;
                                });
                              }
                            }
                          }}
                          className="mt-0.5 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500 h-3.5 w-3.5 cursor-pointer"
                        />
                        <span className="text-xs text-foreground-700 select-none">
                          By submitting you agree to our Privacy Policy
                        </span>
                      </label>
                      {testErrors["privacy"] && (
                        <p className="text-xs text-red-500 mt-1 font-medium">{testErrors["privacy"]}</p>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Success Message Preview Banner */}
            {!testSubmitted && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-4">
                <div className="flex items-center gap-2 mb-1">
                  <i className="ri-checkbox-circle-fill text-green-600" />
                  <span className="text-xs font-semibold text-green-800 uppercase tracking-wide">Success Message Preview</span>
                </div>
                <p className="text-xs text-green-700">{successMessage}</p>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — Field Settings */}
        <div className="w-[300px] bg-white border-l border-gray-200 p-4 overflow-y-auto flex-shrink-0">
          {selectedField ? (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-foreground-950">{selectedField.label}</h3>

              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Label</label>
                <input
                  type="text"
                  value={selectedField.label}
                  onChange={(e) => updateField(selectedField.id, { label: e.target.value })}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 bg-white text-foreground-950"
                  placeholder="e.g. Your Full Name"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Placeholder</label>
                <input
                  type="text"
                  value={selectedField.placeholder}
                  onChange={(e) => updateField(selectedField.id, { placeholder: e.target.value })}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 bg-white text-foreground-950"
                  placeholder="e.g. Enter your name"
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-foreground-700">Required</label>
                <button
                  type="button"
                  onClick={() => updateField(selectedField.id, { required: !selectedField.required })}
                  className={`w-9 h-5 rounded-full transition-colors cursor-pointer relative ${
                    selectedField.required ? "bg-indigo-600" : "bg-gray-200"
                  }`}
                >
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                    selectedField.required ? "left-[18px]" : "left-0.5"
                  }`} />
                </button>
              </div>

              {(selectedField.type === "dropdown" || selectedField.type === "radio" || selectedField.type === "checkbox" || selectedField.type === "property_type" || selectedField.type === "budget_range") && (
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-2">Options</label>
                  <div className="space-y-1.5">
                    {selectedField.options.map((opt, i) => (
                      <div key={i} className="flex items-center gap-1">
                        <input
                          type="text"
                          value={opt}
                          onChange={(e) => updateOption(selectedField.id, i, e.target.value)}
                          className="flex-1 text-sm border border-gray-200 rounded-lg px-2 py-1.5 outline-none focus:border-indigo-500 bg-white text-foreground-950"
                        />
                        <button
                          type="button"
                          onClick={() => removeOption(selectedField.id, i)}
                          className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-50 cursor-pointer transition-colors"
                        >
                          <i className="ri-close-line text-red-400 text-xs" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => addOption(selectedField.id)}
                    className="text-xs text-indigo-600 hover:text-indigo-700 mt-2 cursor-pointer font-medium"
                  >
                    + Add Option
                  </button>
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-2">Width</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => updateField(selectedField.id, { width: "full" })}
                    className={`flex-1 px-3 py-1.5 text-xs rounded-lg cursor-pointer transition-colors whitespace-nowrap ${
                      selectedField.width === "full" ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    Full Width
                  </button>
                  <button
                    type="button"
                    onClick={() => updateField(selectedField.id, { width: "half" })}
                    className={`flex-1 px-3 py-1.5 text-xs rounded-lg cursor-pointer transition-colors whitespace-nowrap ${
                      selectedField.width === "half" ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    Half Width
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-700 mb-1">Help Text</label>
                <input
                  type="text"
                  value={selectedField.helpText}
                  onChange={(e) => updateField(selectedField.id, { helpText: e.target.value })}
                  className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 bg-white text-foreground-950"
                  placeholder="Small helper text below field"
                />
              </div>

              {selectedField.type === "phone" && (
                <p className="text-xs text-gray-400">Validation: Must be 10 digits</p>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center text-sm text-gray-400 text-center py-6">
              Select a field to edit its settings
            </div>
          )}

          {/* Form-wide settings */}
          <div className="border-t border-gray-100 mt-6 pt-5 space-y-3">
            <h3 className="text-xs font-semibold text-foreground-950 uppercase tracking-wider mb-2">Form Settings</h3>

            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1">Submit Button Text</label>
              <input
                type="text"
                value={submitButtonText}
                onChange={(e) => setSubmitButtonText(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 bg-white text-foreground-950"
              />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-foreground-700">Privacy Policy Checkbox</label>
              <button
                type="button"
                onClick={() => setPrivacyPolicyCheckbox(!privacyPolicyCheckbox)}
                className={`w-9 h-5 rounded-full transition-colors cursor-pointer relative ${
                  privacyPolicyCheckbox ? "bg-indigo-600" : "bg-gray-200"
                }`}
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
                  privacyPolicyCheckbox ? "left-[18px]" : "left-0.5"
                }`} />
              </button>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1">Success Message</label>
              <input
                type="text"
                value={successMessage}
                onChange={(e) => setSuccessMessage(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 bg-white text-foreground-950"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1">Map Submissions to Project</label>
              <select
                value={mappedProjectId || ""}
                onChange={(e) => setMappedProjectId(e.target.value || null)}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-indigo-500 bg-white text-foreground-950"
              >
                <option value="">None — Manual assignment</option>
                {projectsList.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">All submissions will auto-map to this project</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}